import React, { useState } from 'react';
import Navigation from '../components/Navigation';
import Footer from '../components/Footer';
import PromoBanner from '../components/PromoBanner';
import { CheckCircle, Star, Users } from 'lucide-react';

const AdvancedAWSProjectsPage = () => {
  const [expandedModules, setExpandedModules] = useState({});

  const toggleModule = (index) => {
    setExpandedModules(prev => ({
      ...prev,
      [index]: !prev[index]
    }));
  };

  const courseModules = [
    {
      title: "Introduction",
      lessons: [
        "Welcome",
        "Getting Technical Support for these 5 Projects [Slack Community]",
        "Project Costs Breakdown"
      ]
    },
    {
      title: "Project 1: Build a CI/CD Pipeline for the 2048 Game using AWS CodePipeline, ECS, and ECR",
      lessons: [
        "Project Overview",
        "Prerequisites [Install Docker & AWS CLI Login]",
        "Prepare the 2048 Game Code",
        "Set up CodeBuild and CodePipelinefor CI/CD",
        "Test the pipeline",
        "Project Conclusion and Clean-up"
      ]
    },
    {
      title: "Project 2: Stock Market Real-Time Data Analytics Pipeline on AWS",
      lessons: [
        "Project Overview",
        "Setting Up Data Streaming with Amazon Kinesis",
        "Processing Data with AWS Lambda",
        "Query Historical Stock Data using Amazon Athena",
        "Stock Trend Alerts using SNS",
        "Project Conclusion and Clean-up"
      ]
    },
    {
      title: "Project 3: Building & Deploying a Cybersecurity Threat Detection System using Amazon SageMaker",
      lessons: [
        "Project Overview",
        "Preprocess Data and Feature Engineering",
        "Training and Testing a Model using XGBoost",
        "Deploy and Serve the Model",
        "Automating with SageMaker Pipelines",
        "Project Conclusion and Clean-up"
      ]
    },
    {
      title: "Project 4: Multi-Cloud Weather Tracker website with Disaster Recovery using Terraform",
      lessons: [
        "Project Overview",
        "Prerequisites",
        "Define AWS Resources on Terraform",
        "Define Azure Resources using Terraform",
        "Implement Disaster Recovery with Route 53 DNS Failover",
        "Project Conclusion and Clean-up"
      ]
    },
    {
      title: "Project 5: Traditional 3-Tier WordPress website Deployment on AWS",
      lessons: [
        "Project Overview",
        "Networking Setup- Create a VPC with Public and Private Subnets",
        "Networking Setup- Create a NAT Gateway and Security Groups",
        "Database Setup- Create MySQL database and EFS in AWS",
        "Compute Setup- Setting up EC2 instances and Application Load Balancer",
        "Project Conclusion and Clean-up"
      ]
    }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      <PromoBanner />
      <Navigation />
      
      {/* Hero Section */}
      <section className="pt-24 pb-16 bg-gradient-to-br from-blue-50 to-indigo-100">
        <div className="container">
          <div className="max-w-6xl mx-auto">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              {/* Left Side - Course Info */}
              <div>
                <h1 className="text-2xl md:text-3xl font-bold text-cyan-600 mb-6">
                  5 Advanced AWS Projects
                </h1>
                
                <p className="text-lg text-gray-600 mb-4 leading-relaxed">
                  Complete Step-By-Step Guide to five Advanced AWS Projects.
                </p>
                
                <p className="text-lg text-gray-600 mb-4 leading-relaxed">
                  Build these stand-out projects, and add them into your portfolio!
                </p>
                
                <p className="text-lg text-gray-600 mb-6 leading-relaxed">
                  These projects are designed to boost your hands-on cloud experience, and help you get hired.
                </p>
                
                <div className="flex items-center gap-2 mb-8">
                  <Users size={20} className="text-blue-500" />
                  <span className="text-lg font-medium text-gray-700">Over 100 enrolments!</span>
                  <Star size={20} className="text-yellow-500 fill-current" />
                </div>
                
                <a 
                  href="https://learn.zerotocloud.co/p/5-advanced-aws-cloud-projects"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="bg-gradient-to-r from-blue-500 to-indigo-600 text-white px-8 py-4 rounded-lg font-semibold text-lg hover:shadow-lg transition-all duration-200 inline-block text-center"
                >
                  Enroll now for $69
                </a>
              </div>
              
              {/* Right Side - Course Thumbnail */}
              <div className="relative">
                <img 
                  src="/course-covers/4.png"
                  alt="5 Advanced AWS Projects"
                  className="w-full rounded-xl shadow-2xl"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Course Content Section */}
      <section className="py-16 bg-white">
        <div className="container">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl font-bold text-cyan-600 mb-12 text-center">
              Course Content
            </h2>
            
            <div className="space-y-4">
              {courseModules.map((module, index) => (
                <div key={index} className="bg-white rounded-lg border border-gray-200 overflow-hidden shadow-sm hover:shadow-md transition-shadow duration-200">
                  <button
                    onClick={() => toggleModule(index)}
                    className="w-full p-4 flex items-center justify-between hover:bg-gray-50 transition-all duration-200 text-left"
                  >
                    <div className="flex items-center gap-3">
                      <span className="text-blue-600 font-semibold text-lg">
                        {index + 1}.
                      </span>
                      <h3 className="text-gray-900 font-medium">
                        {module.title}
                      </h3>
                    </div>
                    <div className="text-blue-600">
                      <svg width="20" height="20" viewBox="0 0 20 20" fill="currentColor" className={`transform transition-transform duration-200 ${expandedModules[index] ? 'rotate-90' : ''}`}>
                        <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
                      </svg>
                    </div>
                  </button>
                  
                  {expandedModules[index] && (
                    <div className="px-4 pb-4 bg-gray-50 border-t border-gray-200">
                      <ul className="space-y-3 pt-4">
                        {module.lessons.map((lesson, lessonIndex) => (
                          <li key={lessonIndex} className="flex items-start gap-3">
                            <CheckCircle className="text-green-500 mt-0.5 flex-shrink-0" size={16} />
                            <span className="text-gray-700">{lesson}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Ready to Start Learning Section */}
      <section className="py-16 bg-gradient-to-br from-blue-50 to-indigo-100">
        <div className="container">
          <div className="max-w-2xl mx-auto text-center">
            <h2 className="text-2xl font-bold text-cyan-600 mb-6">
              Ready to start learning?
            </h2>
            
            <div className="bg-white rounded-xl p-8 shadow-lg border border-gray-200">
              <div className="mb-6">
                <div className="text-4xl font-bold text-gray-900 mb-2">$69</div>
                <p className="text-gray-600">One-time payment • Lifetime access</p>
              </div>
              
              <a 
                href="https://learn.zerotocloud.co/p/5-advanced-aws-cloud-projects"
                target="_blank"
                rel="noopener noreferrer"
                className="w-full bg-gradient-to-r from-green-500 to-emerald-600 text-white px-8 py-4 rounded-lg font-semibold text-xl hover:shadow-lg transition-all duration-200 mb-4 inline-block text-center"
              >
                Enroll now for $69
              </a>
              
              <div className="flex items-center justify-center gap-2 text-sm text-gray-600">
                <Users size={16} className="text-blue-500" />
                <span>Join 100+ students already enrolled</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default AdvancedAWSProjectsPage;